/**
 * Atlas — the LANDMARKS pool (produced by tools/geo-locations `landmarks`).
 *
 * DO NOT EDIT BY HAND. Regenerate with:  pnpm geo-locations landmarks
 *
 * A worldwide set of genuinely famous landmarks for the `landmarks` scope.
 * Every entry has the Street View camera pointed AT the subject (`heading` =
 * bearing from the pano to the landmark); since landmarks-2 the shipped frame
 * was chosen from multiple candidate panos by a vision model that verified the
 * landmark is actually visible in it. Each carries a factual one-liner
 * (`blurb`) and NO `difficulty` (landmarks have no difficulty — the rich
 * reveal).
 *
 * Sources (free, fact-only): notable places + CC0 short descriptions from
 * Wikidata SPARQL; country/continent + US state from GeoNames (CC-BY 4.0); the
 * nearest OFFICIAL Street View pano (panoId + snapped lat/lng + date) from the
 * free metadata endpoint. We ship only FACTS — never cached panorama imagery.
 * See DATA-LICENSES.md.
 *
 * Stats: 203 landmarks across 56 countries —
 * Africa 18, Asia 45, Europe 62, North America 47, South America 23, Oceania 8.
 */
import type { GeoLocation, LocationPack } from './schema';

// Emitted as annotated GeoLocation[] chunks (see emitLandmarks.ts): one giant
// array literal makes TypeScript infer a union of every element shape and bail
// with TS2590, so we split it. Order is preserved exactly (the daily seed).
const CHUNK_0: GeoLocation[] = [
  {
    id: "eiffel-tower",
    lat: 48.8575449779873,
    lng: 2.293268457991295,
    panoId: "rqB3cgFGcvkuO5j4ke0Niw",
    heading: 47,
    pitch: 5,
    place: "Eiffel Tower, Paris 16 Passy, France",
    shortName: "Eiffel Tower",
    countryCode: "FR",
    country: "France",
    continent: "Europe",
    blurb: "Tower located on the Champ de Mars in Paris, France",
    pools: ["world","Europe","FR","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "louvre-museum",
    lat: 48.86142605210435,
    lng: 2.334578640580229,
    panoId: "ygkq_venN4LA7avIVUTdlw",
    heading: 111,
    pitch: 8,
    place: "Louvre Museum, Paris 01 Louvre, France",
    shortName: "Louvre Museum",
    countryCode: "FR",
    country: "France",
    continent: "Europe",
    blurb: "Art and archeology museum in Paris, France",
    pools: ["world","Europe","FR","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "statue-of-liberty",
    lat: 40.6885811637996,
    lng: -74.04436081764133,
    panoId: "0SoZ4zkfn3UuNRqEBYizpQ",
    heading: 356,
    pitch: 8,
    place: "Statue of Liberty, Battery Park City, New York",
    shortName: "Statue of Liberty",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Monument in New York",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "hagia-sophia",
    lat: 41.00749873284126,
    lng: 28.97896584703767,
    panoId: "IvwJBXSVVUAMGcXM2rKthQ",
    heading: 43,
    pitch: 5,
    place: "Hagia Sophia, Sultanahmet, Turkey",
    shortName: "Hagia Sophia",
    countryCode: "TR",
    country: "Turkey",
    continent: "Asia",
    blurb: "Mosque, museum, and former church in Istanbul, Turkey",
    pools: ["world","Asia","TR","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "machu-picchu",
    lat: -13.16220444142064,
    lng: -72.54558110104,
    panoId: "OzcfJ7IJK-nw4VBdOGStwA",
    heading: 179,
    pitch: 5,
    place: "Machu Picchu, Machupicchu, Peru",
    shortName: "Machu Picchu",
    countryCode: "PE",
    country: "Peru",
    continent: "South America",
    blurb: "15th-century Inca citadel in the Peruvian Andes and UNESCO World Heritage Site",
    pools: ["world","South America","PE","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "colosseum",
    lat: 41.89049688333763,
    lng: 12.49235071743072,
    panoId: "D2JtSTQw8_faxK0QL2cXhw",
    heading: 204,
    pitch: 12,
    place: "Colosseum, Celio, Italy",
    shortName: "Colosseum",
    countryCode: "IT",
    country: "Italy",
    continent: "Europe",
    blurb: "Ancient Roman amphitheatre, a landmark of Rome, Italy",
    pools: ["world","Europe","IT","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "white-house",
    lat: 38.89635279291029,
    lng: -77.03666940803113,
    panoId: "F6M7oHm0CsZ1vV_or9JNvA",
    heading: 0,
    pitch: 5,
    place: "White House, Washington, District of Columbia",
    shortName: "White House",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "DC",
    state: "District of Columbia",
    blurb: "Official residence and office of the President of the United States",
    pools: ["world","North America","US","US-DC","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "angkor-wat",
    lat: 13.4125084198175,
    lng: 103.8654652339519,
    panoId: "o6SZLr8rEFZ6F4nmwMATPw",
    heading: 90,
    pitch: 5,
    place: "Angkor Wat, Siem Reap, Cambodia",
    shortName: "Angkor Wat",
    countryCode: "KH",
    country: "Cambodia",
    continent: "Asia",
    blurb: "Hindu-Buddhist temple complex in Cambodia",
    pools: ["world","Asia","KH","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "stonehenge",
    lat: 51.17887136763489,
    lng: -1.826093994334741,
    panoId: "47M60tjRjnQC57Z7sgoBcQ",
    heading: 329,
    pitch: 12,
    place: "Stonehenge, Lark Hill, United Kingdom",
    shortName: "Stonehenge",
    countryCode: "GB",
    country: "United Kingdom",
    continent: "Europe",
    blurb: "Neolithic henge monument in Amesbury, Wiltshire, England, UK",
    pools: ["world","Europe","GB","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "niagara-falls",
    lat: 43.08737885346004,
    lng: -79.07135445187149,
    panoId: "JvhJsJ6oiAGMnJW7Dficjw",
    heading: 178,
    pitch: 0,
    place: "Niagara Falls, Niagara Falls, New York",
    shortName: "Niagara Falls",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Niagara Falls is a three waterfalls that straddle the international border between canada and the united states in Niagara Falls, New York.",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "great-pyramid-of-giza",
    lat: 29.97890701112987,
    lng: 31.13575271692216,
    panoId: "InOiW6C01JTX_W4ecLW23A",
    heading: 280,
    pitch: 5,
    place: "Great Pyramid of Giza, Kirdāsah, Egypt",
    shortName: "Great Pyramid of Giza",
    countryCode: "EG",
    country: "Egypt",
    continent: "Africa",
    blurb: "Oldest and largest of the three pyramids in the Giza Necropolis; one of the seven ancient wonders of the world",
    pools: ["world","Africa","EG","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "musee-dorsay",
    lat: 48.86070516133366,
    lng: 2.327735295431808,
    panoId: "1zwdYrba2835FKxSufunrw",
    heading: 227,
    pitch: 5,
    place: "Musée d'Orsay, Paris 07 Palais-Bourbon, France",
    shortName: "Musée d'Orsay",
    countryCode: "FR",
    country: "France",
    continent: "Europe",
    blurb: "Art museum in Paris, France",
    pools: ["world","Europe","FR","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "grand-canyon",
    lat: 36.09181729086168,
    lng: -112.0902128081111,
    panoId: "qNVoyQvK58VtCKvt6elt5Q",
    heading: 324,
    pitch: 0,
    place: "Grand Canyon, Grand Canyon, Arizona",
    shortName: "Grand Canyon",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "AZ",
    state: "Arizona",
    blurb: "Steep-sided canyon carved by the Colorado River in Arizona, United States",
    pools: ["world","North America","US","US-AZ","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "golden-gate-bridge",
    lat: 37.82091985520957,
    lng: -122.4786430774146,
    panoId: "sOLvT9SEPry_wDx7IGL1VA",
    heading: 179,
    pitch: 5,
    place: "Golden Gate Bridge, Sausalito, California",
    shortName: "Golden Gate Bridge",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "CA",
    state: "California",
    blurb: "Suspension bridge on the San Francisco Bay",
    pools: ["world","North America","US","US-CA","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "palace-of-versailles",
    lat: 48.8047168,
    lng: 2.1202828,
    panoId: "M2fEH1PEsSwT-sl_EXmIbw",
    heading: 329,
    pitch: 12,
    place: "Palace of Versailles, Versailles, France",
    shortName: "Palace of Versailles",
    countryCode: "FR",
    country: "France",
    continent: "Europe",
    blurb: "Palace in Versailles, France and location of the Museum of the History of France",
    pools: ["world","Europe","FR","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "chichen-itza",
    lat: 20.68336609171633,
    lng: -88.56850715125286,
    panoId: "R_wd9gQHE1a4AqwRdnC0OQ",
    heading: 197,
    pitch: 12,
    place: "Chichen Itza, Chichén-Itzá, Mexico",
    shortName: "Chichen Itza",
    countryCode: "MX",
    country: "Mexico",
    continent: "North America",
    blurb: "Pre-Columbian Maya city in Mexico",
    pools: ["world","North America","MX","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "british-museum",
    lat: 51.51861607965417,
    lng: -0.1256437152675574,
    panoId: "Hfhy5bQkX-KLzAFLrx3evg",
    heading: 316,
    pitch: 5,
    place: "British Museum, Holborn, United Kingdom",
    shortName: "British Museum",
    countryCode: "GB",
    country: "United Kingdom",
    continent: "Europe",
    blurb: "National museum in London, United Kingdom",
    pools: ["world","Europe","GB","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "chateau-de-montsoreau",
    lat: 47.21466495934417,
    lng: 0.06225614230475908,
    panoId: "foAdTgTKlP7l1Rdq0xVP2A",
    heading: 358,
    pitch: 8,
    place: "Château de Montsoreau, Varennes-sur-Loire, France",
    shortName: "Château de Montsoreau",
    countryCode: "FR",
    country: "France",
    continent: "Europe",
    blurb: "Castle in the Loire valley France and location of château de montsoreau-museum of contemporary art",
    pools: ["world","Europe","FR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "red-square",
    lat: 55.7541625548625,
    lng: 37.62001516343054,
    panoId: "QH-UXxi27BHzEW__dluRTg",
    heading: 296,
    pitch: 12,
    place: "Red Square, Moscow, Russia",
    shortName: "Red Square",
    countryCode: "RU",
    country: "Russia",
    continent: "Europe",
    blurb: "The main square in Moscow, Russia",
    pools: ["world","Europe","RU","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "borobudur-temple",
    lat: -7.607933083496086,
    lng: 110.2038411959255,
    panoId: "mjbhnsTkOf76axayalVeww",
    heading: 339,
    pitch: 12,
    place: "Borobudur Temple, Mungkid, Indonesia",
    shortName: "Borobudur Temple",
    countryCode: "ID",
    country: "Indonesia",
    continent: "Asia",
    blurb: "Buddhist temple in Java, Indonesia",
    pools: ["world","Asia","ID","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "sydney-opera-house",
    lat: -33.85628095783255,
    lng: 151.2157737822349,
    panoId: "iOhm7Py3wF_3QtHR33ciNQ",
    heading: 223,
    pitch: 8,
    place: "Sydney Opera House, The Rocks, Australia",
    shortName: "Sydney Opera House",
    countryCode: "AU",
    country: "Australia",
    continent: "Oceania",
    blurb: "Multi-venue performing arts centre in Sydney, New South Wales, Australia",
    pools: ["world","Oceania","AU","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "iguacu-falls",
    lat: -25.69560596509938,
    lng: -54.43728605258735,
    panoId: "Km-U5BkmGn2Yopa3BrryKQ",
    heading: 60,
    pitch: 0,
    place: "Iguaçu Falls, Puerto Iguazú, Argentina",
    shortName: "Iguaçu Falls",
    countryCode: "AR",
    country: "Argentina",
    continent: "South America",
    blurb: "Waterfalls on the Igazú River on the border of Misiones, Argentina and Paraná, Brazil",
    pools: ["world","South America","AR","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "alhambra",
    lat: 37.17634952639604,
    lng: -3.589661641998135,
    panoId: "6nckds5xJcp7mPjiZTtc1w",
    heading: 90,
    pitch: 5,
    place: "Alhambra, Granada, Spain",
    shortName: "Alhambra",
    countryCode: "ES",
    country: "Spain",
    continent: "Europe",
    blurb: "Palace and fortress complex in Granada, Andalusia, Spain",
    pools: ["world","Europe","ES","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "christ-the-redeemer",
    lat: -22.95186347182485,
    lng: -43.20998327832908,
    panoId: "Y2O4kFnXFRJUmlkE0Nx5lQ",
    heading: 263,
    pitch: 12,
    place: "Christ the Redeemer, Copacabana, Brazil",
    shortName: "Christ the Redeemer",
    countryCode: "BR",
    country: "Brazil",
    continent: "South America",
    blurb: "Colossal statue of Jesus in Rio de Janeiro, Brazil",
    pools: ["world","South America","BR","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "sultan-ahmed-mosque",
    lat: 41.00550132253288,
    lng: 28.97661833436421,
    panoId: "O86qN5G7KT0qPTv_yNyHZA",
    heading: 127,
    pitch: 12,
    place: "Sultan Ahmed Mosque, Sultanahmet, Turkey",
    shortName: "Sultan Ahmed Mosque",
    countryCode: "TR",
    country: "Turkey",
    continent: "Asia",
    blurb: "Historic mosque located in Istanbul, Turkey, also known as Blue Mosque",
    pools: ["world","Asia","TR","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "tower-of-london",
    lat: 51.50712570435068,
    lng: -0.07612051593763661,
    panoId: "GF5M7N_v0FOlwZAkCCMj3Q",
    heading: 357,
    pitch: 8,
    place: "Tower of London, Whitechapel, United Kingdom",
    shortName: "Tower of London",
    countryCode: "GB",
    country: "United Kingdom",
    continent: "Europe",
    blurb: "Castle in central London, United Kingdom",
    pools: ["world","Europe","GB","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "metropolitan-museum-of-art",
    lat: 40.77871977488655,
    lng: -73.96250138857499,
    panoId: "IyOWhJXjjYB_EX6ThbdwsQ",
    heading: 319,
    pitch: 8,
    place: "Metropolitan Museum of Art, Manhattan, New York",
    shortName: "Metropolitan Museum of Art",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Art museum in New York City, United States",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "hermitage-museum",
    lat: 59.94162894912355,
    lng: 30.31378933565909,
    panoId: "vooruzER4cfoK01_n8E9Hg",
    heading: 185,
    pitch: 8,
    place: "Hermitage Museum, Saint Petersburg, Russia",
    shortName: "Hermitage Museum",
    countryCode: "RU",
    country: "Russia",
    continent: "Europe",
    blurb: "Museum in Saint Petersburg, Russia",
    pools: ["world","Europe","RU","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "bastille",
    lat: 48.85312360051785,
    lng: 2.370590598640281,
    panoId: "Ihqorn82t5ZvL07z3IkWHw",
    heading: 283,
    pitch: 8,
    place: "Bastille, Quinze-Vingts, France",
    shortName: "Bastille",
    countryCode: "FR",
    country: "France",
    continent: "Europe",
    blurb: "Former fortress in Paris, France",
    pools: ["world","Europe","FR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "tower-bridge",
    lat: 51.50611258411194,
    lng: -0.07662195215925749,
    panoId: "gSyTqVCNpg9yp8XSD1BsSg",
    heading: 124,
    pitch: 8,
    place: "Tower Bridge, Whitechapel, United Kingdom",
    shortName: "Tower Bridge",
    countryCode: "GB",
    country: "United Kingdom",
    continent: "Europe",
    blurb: "Bascule and suspension bridge in London",
    pools: ["world","Europe","GB","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "egyptian-pyramids",
    lat: 29.97253316959818,
    lng: 31.12748793075468,
    panoId: "n_bPJFjeyjWI1P3OXhuqJg",
    heading: 93,
    pitch: 8,
    place: "Egyptian pyramids, Kirdāsah, Egypt",
    shortName: "Egyptian pyramids",
    countryCode: "EG",
    country: "Egypt",
    continent: "Africa",
    blurb: "Egyptian pyramids is an ancient masonry structures constructed by ancient egyptians in Kirdāsah, Egypt.",
    pools: ["world","Africa","EG","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "windsor-castle",
    lat: 51.48278662114362,
    lng: -0.6064922435848403,
    panoId: "hTnd99B5V8CeVvylSAh32w",
    heading: 49,
    pitch: 5,
    place: "Windsor Castle, Windsor, United Kingdom",
    shortName: "Windsor Castle",
    countryCode: "GB",
    country: "United Kingdom",
    continent: "Europe",
    blurb: "Historic castle, royal residence at Windsor in Berkshire, England, UK",
    pools: ["world","Europe","GB","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "brooklyn-bridge",
    lat: 40.70503542121962,
    lng: -73.99674712294534,
    panoId: "1u60DivmR_Bq0HcUs_bmtw",
    heading: 26,
    pitch: 8,
    place: "Brooklyn Bridge, Financial District, New York",
    shortName: "Brooklyn Bridge",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Bridge in New York City, crossing the East River",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "cologne-cathedral",
    lat: 50.94025002647148,
    lng: 6.958177436765252,
    panoId: "pei93_YF_TELk-GDeh_4qw",
    heading: 5,
    pitch: 5,
    place: "Cologne Cathedral, Altstadt Nord, Germany",
    shortName: "Cologne Cathedral",
    countryCode: "DE",
    country: "Germany",
    continent: "Europe",
    blurb: "Roman-Catholic cathedral in Cologne, Germany",
    pools: ["world","Europe","DE","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "taipei-101",
    lat: 25.03284161051275,
    lng: 121.5656176005734,
    panoId: "YILsaCnx0DeCDf-yw2ZUpw",
    heading: 313,
    pitch: 5,
    place: "Taipei 101, Taipei, Taiwan",
    shortName: "Taipei 101",
    countryCode: "TW",
    country: "Taiwan",
    continent: "Asia",
    blurb: "Skyscraper located in Xinyi District, Taipei, Taiwan",
    pools: ["world","Asia","TW","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "mode-gakuen-cocoon-tower",
    lat: 35.69161894022499,
    lng: 139.6982461612731,
    panoId: "TXckNgHxlNBH5ECeA3W7Ew",
    heading: 273,
    pitch: 8,
    place: "Mode Gakuen Cocoon Tower, Tokyo, Japan",
    shortName: "Mode Gakuen Cocoon Tower",
    countryCode: "JP",
    country: "Japan",
    continent: "Asia",
    blurb: "Building in Tokyo, Japan",
    pools: ["world","Asia","JP","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "the-great-sphinx",
    lat: 29.97503017543027,
    lng: 31.13760525597368,
    panoId: "7l75smvZvVz9u-F4i7gUSA",
    heading: 15,
    pitch: 12,
    place: "The Great Sphinx, Abū an Numrus, Egypt",
    shortName: "The Great Sphinx",
    countryCode: "EG",
    country: "Egypt",
    continent: "Africa",
    blurb: "The Great Sphinx is an ancient egyptian monumental sculpture in Abū an Numrus, Egypt.",
    pools: ["world","Africa","EG","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "western-wall",
    lat: 31.77675391245674,
    lng: 35.23400998867629,
    panoId: "L-pNQoeperPBe_KLJbhTaQ",
    heading: 47,
    pitch: 12,
    place: "Western Wall, Old City, Palestinian Territory",
    shortName: "Western Wall",
    countryCode: "PS",
    country: "Palestinian Territory",
    continent: "Asia",
    blurb: "Holy site of Judaism in Jerusalem",
    pools: ["world","Asia","PS","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "ajanta-caves",
    lat: 20.55243455885976,
    lng: 75.70118157020639,
    panoId: "c5SHgFYE6a91HgbpgfgKJw",
    heading: 326,
    pitch: 5,
    place: "Ajanta Caves, Soygaon, India",
    shortName: "Ajanta Caves",
    countryCode: "IN",
    country: "India",
    continent: "Asia",
    blurb: "2nd century BCE to 1st century CE Buddhist cave monuments located in Maharashtra, India",
    pools: ["world","Asia","IN","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "cn-tower",
    lat: 43.64198016587903,
    lng: -79.38785122697212,
    panoId: "Oky4vn6j_vkSZHc0S9yaBg",
    heading: 33,
    pitch: 8,
    place: "CN Tower, Waterfront Communities-The Island, Canada",
    shortName: "CN Tower",
    countryCode: "CA",
    country: "Canada",
    continent: "North America",
    blurb: "Communications and observation tower in Toronto, Canada",
    pools: ["world","North America","CA","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "london-eye",
    lat: 51.50259507685178,
    lng: -0.1210781680766786,
    panoId: "ZD8roqmntQ8EMPEFicOTvA",
    heading: 49,
    pitch: 5,
    place: "London Eye, London, United Kingdom",
    shortName: "London Eye",
    countryCode: "GB",
    country: "United Kingdom",
    continent: "Europe",
    blurb: "Observation wheel in London, England",
    pools: ["world","Europe","GB","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "teotihuacan",
    lat: 19.69284143370012,
    lng: -98.84486493226902,
    panoId: "Pk8LT9bEF8jmbtH74_gmTQ",
    heading: 110,
    pitch: 8,
    place: "Teotihuacan, San Francisco Mazapa, Mexico",
    shortName: "Teotihuacan",
    countryCode: "MX",
    country: "Mexico",
    continent: "North America",
    blurb: "Teotihuacan is an ancient mesoamerican city in San Francisco Mazapa, Mexico.",
    pools: ["world","North America","MX","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "milan-cathedral",
    lat: 45.46394701405627,
    lng: 9.188986748739012,
    panoId: "zPBgkPoYZZBXjDeY3bC3WA",
    heading: 89,
    pitch: 5,
    place: "Milan Cathedral, Milan, Italy",
    shortName: "Milan Cathedral",
    countryCode: "IT",
    country: "Italy",
    continent: "Europe",
    blurb: "Cathedral church of Milan, Italy",
    pools: ["world","Europe","IT","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "bolshoi-theatre",
    lat: 55.75909192945273,
    lng: 37.61857067445965,
    panoId: "CWDDHkGY1Iw29j2yROyQ9w",
    heading: 1,
    pitch: 5,
    place: "Bolshoi Theatre, Moscow, Russia",
    shortName: "Bolshoi Theatre",
    countryCode: "RU",
    country: "Russia",
    continent: "Europe",
    blurb: "Historic theatre in Moscow, Russia",
    pools: ["world","Europe","RU","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "church-of-the-holy-sepulchre",
    lat: 31.77806549218072,
    lng: 35.22982309499679,
    panoId: "M_dRdNjJdfwZnU_b0AMxFw",
    heading: 351,
    pitch: 12,
    place: "Church of the Holy Sepulchre, Old City, Palestinian Territory",
    shortName: "Church of the Holy Sepulchre",
    countryCode: "PS",
    country: "Palestinian Territory",
    continent: "Asia",
    blurb: "Church in Jerusalem, containing the two holiest sites in Christianity",
    pools: ["world","Asia","PS","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "tikal",
    lat: 17.22206804877612,
    lng: -89.62357740586097,
    panoId: "WEhcwYUuefNkXQTvmMg48A",
    heading: 307,
    pitch: 12,
    place: "Tikal, Guatemala",
    shortName: "Tikal",
    countryCode: "GT",
    country: "Guatemala",
    continent: "North America",
    blurb: "Ruins of major ancient Maya city",
    pools: ["world","North America","GT","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "winter-palace",
    lat: 59.9389160158006,
    lng: 30.31298211838575,
    panoId: "wfuWCw1saCW_dmXcpxec4A",
    heading: 41,
    pitch: 5,
    place: "Winter Palace, Saint Petersburg, Russia",
    shortName: "Winter Palace",
    countryCode: "RU",
    country: "Russia",
    continent: "Europe",
    blurb: "Former imperial palace in Saint Petersburg, Russia",
    pools: ["world","Europe","RU","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "ellora-caves",
    lat: 20.02543359885274,
    lng: 75.17810915664654,
    panoId: "xWcfiI_Nt8UAAAGu4olnQg",
    heading: 46,
    pitch: 5,
    place: "Ellora Caves, Khuldābād, India",
    shortName: "Ellora Caves",
    countryCode: "IN",
    country: "India",
    continent: "Asia",
    blurb: "Ancient cave temples of Hinduism, Buddhism and Jainism in Maharashtra, India",
    pools: ["world","Asia","IN","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "trafalgar-square",
    lat: 51.50807164794632,
    lng: -0.1280728928028963,
    panoId: "8YMtK2fn8o-dPv2Ao1l5GA",
    heading: 146,
    pitch: 12,
    place: "Trafalgar Square, London, United Kingdom",
    shortName: "Trafalgar Square",
    countryCode: "GB",
    country: "United Kingdom",
    continent: "Europe",
    blurb: "Public space and tourist attraction in central London",
    pools: ["world","Europe","GB","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "uffizi-gallery",
    lat: 43.7695272414545,
    lng: 11.25530362786756,
    panoId: "XxHDNMpMT-05FiWdi-GYJQ",
    heading: 181,
    pitch: 5,
    place: "Uffizi Gallery, Florence, Italy",
    shortName: "Uffizi Gallery",
    countryCode: "IT",
    country: "Italy",
    continent: "Europe",
    blurb: "Palace and art museum in Florence, Italy",
    pools: ["world","Europe","IT","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "times-square",
    lat: 40.75754869676104,
    lng: -73.98584123504942,
    panoId: "Aw1tXg6lo21PuIMM9GC4Qg",
    heading: 173,
    pitch: 12,
    place: "Times Square, Times Square, New York",
    shortName: "Times Square",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Neighborhood and square in Manhattan, New York City, United States",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "roman-forum",
    lat: 41.89310017178297,
    lng: 12.48430344418062,
    panoId: "AE2WcUkwbYRY9tnc6-P11w",
    heading: 143,
    pitch: 5,
    place: "Roman Forum, Pigna, Italy",
    shortName: "Roman Forum",
    countryCode: "IT",
    country: "Italy",
    continent: "Europe",
    blurb: "Ancient Roman center of the city, a landmark of Rome, Italy",
    pools: ["world","Europe","IT","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "national-gallery",
    lat: 51.5077135621674,
    lng: -0.1283005037240436,
    panoId: "a7laVgbimYgm-fdoIWK8gA",
    heading: 359,
    pitch: 5,
    place: "National Gallery, London, United Kingdom",
    shortName: "National Gallery",
    countryCode: "GB",
    country: "United Kingdom",
    continent: "Europe",
    blurb: "Art museum in London, England, UK",
    pools: ["world","Europe","GB","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "smithsonian-institution",
    lat: 38.89006248470998,
    lng: -77.02592564065775,
    panoId: "j0twgtJmaqUz0amnh1F57g",
    heading: 183,
    pitch: 5,
    place: "Smithsonian Institution, Downtown DC, District of Columbia",
    shortName: "Smithsonian Institution",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "DC",
    state: "District of Columbia",
    blurb: "Group of museums and research centers administered by the United States government",
    pools: ["world","North America","US","US-DC","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "washington-monument",
    lat: 38.88927361429375,
    lng: -77.03531998238401,
    panoId: "c_SNp-fGSFPWglXGRImpyw",
    heading: 16,
    pitch: 12,
    place: "Washington Monument, Washington, District of Columbia",
    shortName: "Washington Monument",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "DC",
    state: "District of Columbia",
    blurb: "Obelisk on the National Mall in Washington, D.C.",
    pools: ["world","North America","US","US-DC","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "akashi-kaikyo-bridge",
    lat: 34.61464881188677,
    lng: 135.0192877790895,
    panoId: "fm5mT47eGsZMrUrzK0GtHw",
    heading: 25,
    pitch: 5,
    place: "Akashi Kaikyō Bridge, Akashi, Japan",
    shortName: "Akashi Kaikyō Bridge",
    countryCode: "JP",
    country: "Japan",
    continent: "Asia",
    blurb: "Suspension bridge in Kobe, Japan",
    pools: ["world","Asia","JP","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "masada",
    lat: 31.31469258656109,
    lng: 35.35460221530616,
    panoId: "m7iQwr15CTEAAAQYVntUqg",
    heading: 325,
    pitch: 8,
    place: "Masada, ‘En Boqeq, Israel",
    shortName: "Masada",
    countryCode: "IL",
    country: "Israel",
    continent: "Asia",
    blurb: "Archaeological site in southeastern Israel",
    pools: ["world","Asia","IL","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "florence-cathedral",
    lat: 43.77286080358763,
    lng: 11.2566215711893,
    panoId: "8IFr3fD9JwQ4dME3N57vUQ",
    heading: 50,
    pitch: 12,
    place: "Florence Cathedral, Florence, Italy",
    shortName: "Florence Cathedral",
    countryCode: "IT",
    country: "Italy",
    continent: "Europe",
    blurb: "13th-century cathedral in Florence, Italy",
    pools: ["world","Europe","IT","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "badshahi-mosque",
    lat: 31.58875819205446,
    lng: 74.30937690559153,
    panoId: "Qe0o-kqzn9EAAAQukDPQ0Q",
    heading: 175,
    pitch: 8,
    place: "Badshahi Mosque, Lahore, Pakistan",
    shortName: "Badshahi Mosque",
    countryCode: "PK",
    country: "Pakistan",
    continent: "Asia",
    blurb: "Mosque in Lahore, Pakistan",
    pools: ["world","Asia","PK","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "guggenheim-museum-bilbao",
    lat: 43.26893999933914,
    lng: -2.935208806772067,
    panoId: "aiAvPUUmaWlTVJWvIpF2jA",
    heading: 98,
    pitch: 8,
    place: "Guggenheim Museum Bilbao, Bilbao, Spain",
    shortName: "Guggenheim Museum Bilbao",
    countryCode: "ES",
    country: "Spain",
    continent: "Europe",
    blurb: "Museum of modern and contemporary art in Bilbao, Spain",
    pools: ["world","Europe","ES","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "selimiye-mosque",
    lat: 41.67704023898386,
    lng: 26.55943392160071,
    panoId: "FJEsVFCF6Hl1qo5HLgH1SQ",
    heading: 0,
    pitch: 8,
    place: "Selimiye Mosque, Edirne, Turkey",
    shortName: "Selimiye Mosque",
    countryCode: "TR",
    country: "Turkey",
    continent: "Asia",
    blurb: "Mosque in Edirne, northwestern Turkey",
    pools: ["world","Asia","TR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "palenque",
    lat: 17.48349480446815,
    lng: -92.04670442564542,
    panoId: "xsQsFlwnxNEBCjMY34Nieg",
    heading: 24,
    pitch: 8,
    place: "Palenque, Palenque, Mexico",
    shortName: "Palenque",
    countryCode: "MX",
    country: "Mexico",
    continent: "North America",
    blurb: "Former Maya city state in southern Mexico",
    pools: ["world","North America","MX","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "saint-peters-square",
    lat: 41.90236805248888,
    lng: 12.45848224624272,
    panoId: "_zlnB5FxZp3bYrtBL15_2Q",
    heading: 259,
    pitch: 8,
    place: "Saint Peter's Square, Vatican City, Vatican",
    shortName: "Saint Peter's Square",
    countryCode: "VA",
    country: "Vatican",
    continent: "Europe",
    blurb: "Piazza in Vatican City",
    pools: ["world","Europe","VA","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "cathedral-of-santiago-de-compostela",
    lat: 42.88132491920981,
    lng: -8.544189872030444,
    panoId: "jdJtmr3u3e8oXgRKlG2ubg",
    heading: 194,
    pitch: 8,
    place: "Cathedral of Santiago de Compostela, Santiago de Compostela, Spain",
    shortName: "Cathedral of Santiago de Compostela",
    countryCode: "ES",
    country: "Spain",
    continent: "Europe",
    blurb: "Roman Catholic cathedral of the archdiocese of Santiago de Compostela in Galicia, Spain",
    pools: ["world","Europe","ES","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "centre-georges-pompidou",
    lat: 48.86143873196866,
    lng: 2.353616794440051,
    panoId: "vPUQKIdeQk-durz2KfpoDA",
    heading: 229,
    pitch: 5,
    place: "Centre Georges Pompidou, Paris 04 Hôtel-de-Ville, France",
    shortName: "Centre Georges Pompidou",
    countryCode: "FR",
    country: "France",
    continent: "Europe",
    blurb: "National cultural center of contemporary art in Paris, France",
    pools: ["world","Europe","FR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "prague-castle",
    lat: 50.08929625713293,
    lng: 14.39863904652778,
    panoId: "jKjvUMPthYd6uRA-mfCcgQ",
    heading: 51,
    pitch: 5,
    place: "Prague Castle, Malá Strana, Czechia",
    shortName: "Prague Castle",
    countryCode: "CZ",
    country: "Czechia",
    continent: "Europe",
    blurb: "Castle complex in Prague, Czech Republic, dating from the 9th century",
    pools: ["world","Europe","CZ","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "uxmal",
    lat: 20.35945760279497,
    lng: -89.77017578566158,
    panoId: "tLdLlmszQIx9VTdqHXIJrA",
    heading: 269,
    pitch: 5,
    place: "Uxmal, Santa Elena, Mexico",
    shortName: "Uxmal",
    countryCode: "MX",
    country: "Mexico",
    continent: "North America",
    blurb: "Pre-Columbian Maya city in Yucatan",
    pools: ["world","North America","MX","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "seville-cathedral",
    lat: 37.38570566691011,
    lng: -5.993064304542088,
    panoId: "OHG0g49yydiCpMkKUafejA",
    heading: 326,
    pitch: 12,
    place: "Seville Cathedral, Sevilla, Spain",
    shortName: "Seville Cathedral",
    countryCode: "ES",
    country: "Spain",
    continent: "Europe",
    blurb: "Catholic Cathedral in Seville (Formerly a Mosque), Spain",
    pools: ["world","Europe","ES","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "palais-garnier",
    lat: 48.87191619640376,
    lng: 2.33333380851718,
    panoId: "FKfM_hL6aELBfaV5ledl8g",
    heading: 271,
    pitch: 5,
    place: "Palais Garnier, Paris 09 Opéra, France",
    shortName: "Palais Garnier",
    countryCode: "FR",
    country: "France",
    continent: "Europe",
    blurb: "Opera house in Paris, France",
    pools: ["world","Europe","FR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "place-de-la-concorde",
    lat: 48.86574617043309,
    lng: 2.3209525697676,
    panoId: "87hhsQsHkzzn-IUkTt6_fg",
    heading: 121,
    pitch: 12,
    place: "place de la Concorde, Paris 07 Palais-Bourbon, France",
    shortName: "place de la Concorde",
    countryCode: "FR",
    country: "France",
    continent: "Europe",
    blurb: "Square in the 8th arrondissement of Paris, France",
    pools: ["world","Europe","FR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "chateau-de-montsoreau-museum-of-contemporary-art",
    lat: 47.21569508626082,
    lng: 0.06289310464221053,
    panoId: "RPOSen-ymiG3zVkZ2K_4rw",
    heading: 253,
    pitch: 8,
    place: "Château de Montsoreau-Museum of Contemporary Art, Varennes-sur-Loire, France",
    shortName: "Château de Montsoreau-Museum of Contemporary Art",
    countryCode: "FR",
    country: "France",
    continent: "Europe",
    blurb: "Museum of Contemporary art in France",
    pools: ["world","Europe","FR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "pergamon-museum",
    lat: 52.51987717603866,
    lng: 13.39608118862803,
    panoId: "ZNU5GwELC7GNzvrQrjpgsw",
    heading: 357,
    pitch: 5,
    place: "Pergamon Museum, Mitte, Germany",
    shortName: "Pergamon Museum",
    countryCode: "DE",
    country: "Germany",
    continent: "Europe",
    blurb: "Museum building in Berlin, part of the Museumsinsel, presents the Vorderasiatisches Museum, the Museum für Islamische Kunst and parts of the Antikensammlung",
    pools: ["world","Europe","DE","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "royal-observatory",
    lat: 51.47885923941317,
    lng: -0.001429613584757989,
    panoId: "oXjZIuL7juTN0CsFqGtT8w",
    heading: 179,
    pitch: 8,
    place: "Royal Observatory, Greenwich, United Kingdom",
    shortName: "Royal Observatory",
    countryCode: "GB",
    country: "United Kingdom",
    continent: "Europe",
    blurb: "Observatory in Greenwich, London, UK",
    pools: ["world","Europe","GB","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "tate-modern",
    lat: 51.5066532555992,
    lng: -0.0994463774203022,
    panoId: "4glrHM-5QyKy6mkQWkCT0Q",
    heading: 0,
    pitch: 5,
    place: "Tate Modern, City of London, United Kingdom",
    shortName: "Tate Modern",
    countryCode: "GB",
    country: "United Kingdom",
    continent: "Europe",
    blurb: "Modern art gallery located in Bankside, London, England",
    pools: ["world","Europe","GB","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "mosque-cathedral-of-cordoba",
    lat: 37.87884669540274,
    lng: -4.779379942436833,
    panoId: "TSC4-lqIDKXYm2o1Asij5w",
    heading: 58,
    pitch: 12,
    place: "Mosque-Cathedral of Cordoba, Córdoba, Spain",
    shortName: "Mosque-Cathedral of Cordoba",
    countryCode: "ES",
    country: "Spain",
    continent: "Europe",
    blurb: "Cathedral (former mosque) in Cordoba, Spain",
    pools: ["world","Europe","ES","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "saint-sophia-cathedral",
    lat: 50.45281008167376,
    lng: 30.51618129014161,
    panoId: "WB7Xvu8f1BoRdgm2m8V1DQ",
    heading: 268,
    pitch: 5,
    place: "Saint Sophia Cathedral, Kyiv, Ukraine",
    shortName: "Saint Sophia Cathedral",
    countryCode: "UA",
    country: "Ukraine",
    continent: "Europe",
    blurb: "Cathedral in Kyiv, Ukraine",
    pools: ["world","Europe","UA","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "ostankino-tower",
    lat: 55.81973187599652,
    lng: 37.61447702823259,
    panoId: "6ORxBbWM4XYLo5ydKvskmw",
    heading: 270,
    pitch: 5,
    place: "Ostankino Tower, Ostankinskiy, Russia",
    shortName: "Ostankino Tower",
    countryCode: "RU",
    country: "Russia",
    continent: "Europe",
    blurb: "Television and radio tower in Moscow, Russia",
    pools: ["world","Europe","RU","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "jama-masjid-delhi",
    lat: 28.6516711154087,
    lng: 77.23336644722067,
    panoId: "HB1621pqeDVZ6M4g173Dpg",
    heading: 198,
    pitch: 8,
    place: "Jama Masjid, Delhi, Delhi, India",
    shortName: "Jama Masjid, Delhi",
    countryCode: "IN",
    country: "India",
    continent: "Asia",
    blurb: "Mosque in Delhi, India",
    pools: ["world","Asia","IN","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "doges-palace",
    lat: 45.43262387401845,
    lng: 12.34039020569546,
    panoId: "ZVuP0uJjCorUCraAq8pNQA",
    heading: 1,
    pitch: 8,
    place: "Doge's Palace, Venice, Italy",
    shortName: "Doge's Palace",
    countryCode: "IT",
    country: "Italy",
    continent: "Europe",
    blurb: "Palace in Venice, Italy",
    pools: ["world","Europe","IT","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "drottningholm-palace",
    lat: 59.32280100055781,
    lng: 17.88796973880892,
    panoId: "fTuKvDwQ0a6s0Q5w0bX_jg",
    heading: 216,
    pitch: 5,
    place: "Drottningholm Palace, Bromma, Sweden",
    shortName: "Drottningholm Palace",
    countryCode: "SE",
    country: "Sweden",
    continent: "Europe",
    blurb: "Royal palace in Ekerö Municipality, Sweden",
    pools: ["world","Europe","SE","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "national-library-of-australia",
    lat: -35.29736306816272,
    lng: 149.1299781232298,
    panoId: "dCrjF8u3ElMkbTCkYRBOHg",
    heading: 336,
    pitch: 8,
    place: "National Library of Australia, Barton, Australia",
    shortName: "National Library of Australia",
    countryCode: "AU",
    country: "Australia",
    continent: "Oceania",
    blurb: "National reference library in Canberra, Australia",
    pools: ["world","Oceania","AU","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "hawa-mahal",
    lat: 26.92419764115448,
    lng: 75.8272723877575,
    panoId: "yTLY0eCwpCslUOXHHL3zWQ",
    heading: 248,
    pitch: 8,
    place: "Hawa Mahal, Jaipur, India",
    shortName: "Hawa Mahal",
    countryCode: "IN",
    country: "India",
    continent: "Asia",
    blurb: "Palace in Jaipur, Rajasthan, India",
    pools: ["world","Asia","IN","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "fernsehturm-berlin",
    lat: 52.51997185516655,
    lng: 13.41078053925914,
    panoId: "zgF0TScscE7ChpyPb142EQ",
    heading: 317,
    pitch: 5,
    place: "Fernsehturm Berlin, Mitte, Germany",
    shortName: "Fernsehturm Berlin",
    countryCode: "DE",
    country: "Germany",
    continent: "Europe",
    blurb: "Television tower in Berlin",
    pools: ["world","Europe","DE","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "palazzo-pitti",
    lat: 43.76572132599808,
    lng: 11.24946521923448,
    panoId: "uhte6J1H9MkXnl31xJ67Qg",
    heading: 145,
    pitch: 8,
    place: "Palazzo Pitti, Florence, Italy",
    shortName: "Palazzo Pitti",
    countryCode: "IT",
    country: "Italy",
    continent: "Europe",
    blurb: "Renaissance palace and museum in Florence, Italy",
    pools: ["world","Europe","IT","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "bosphorus-bridge",
    lat: 41.04472407035644,
    lng: 29.03508984507552,
    panoId: "bVJYoiGg3-DFksHzoF635w",
    heading: 316,
    pitch: 5,
    place: "Bosphorus Bridge, Beylerbeyi, Turkey",
    shortName: "Bosphorus Bridge",
    countryCode: "TR",
    country: "Turkey",
    continent: "Asia",
    blurb: "Bridge in İstanbul, Turkey",
    pools: ["world","Asia","TR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "castel-santangelo",
    lat: 41.90258222087918,
    lng: 12.46727208125096,
    panoId: "zRPh8dOmvPDKk7ZOhbLOeA",
    heading: 303,
    pitch: 8,
    place: "Castel Sant'Angelo, Ponte, Italy",
    shortName: "Castel Sant'Angelo",
    countryCode: "IT",
    country: "Italy",
    continent: "Europe",
    blurb: "Castle and museum in Rome, formerly the imperial mausoleum of the emperor Hadrian and subsequent Roman emperors",
    pools: ["world","Europe","IT","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "malbork-castle",
    lat: 54.03856005875212,
    lng: 19.02773402469686,
    panoId: "WeBEPAltKSx4sC1mYc7JfQ",
    heading: 1,
    pitch: 5,
    place: "Malbork Castle, Malbork, Poland",
    shortName: "Malbork Castle",
    countryCode: "PL",
    country: "Poland",
    continent: "Europe",
    blurb: "Castle built in Prussia (now Poland) by the Teutonic Knights",
    pools: ["world","Europe","PL","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "trajans-column",
    lat: 41.89578590425978,
    lng: 12.4840128739173,
    panoId: "QRcsygaGhdnJf5UrrAsGyA",
    heading: 67,
    pitch: 12,
    place: "Trajan's Column, Pigna, Italy",
    shortName: "Trajan's Column",
    countryCode: "IT",
    country: "Italy",
    continent: "Europe",
    blurb: "Ancient Roman victory column, a landmark of Rome, Italy",
    pools: ["world","Europe","IT","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "saint-isaacs-cathedral",
    lat: 59.93363782080324,
    lng: 30.30522951178354,
    panoId: "DUC9rXd1caSim1NqgJm0hg",
    heading: 41,
    pitch: 8,
    place: "Saint Isaac's Cathedral, Saint Petersburg, Russia",
    shortName: "Saint Isaac's Cathedral",
    countryCode: "RU",
    country: "Russia",
    continent: "Europe",
    blurb: "Russian Orthodox cathedral in St. Petersburg, Russia",
    pools: ["world","Europe","RU","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "great-mosque-of-djenne",
    lat: 13.90528091252046,
    lng: -4.555572356675612,
    panoId: "mfr1QjIbpwUAAARAtEXRUQ",
    heading: 101,
    pitch: 12,
    place: "Great Mosque of Djenné, Djenné, Mali",
    shortName: "Great Mosque of Djenné",
    countryCode: "ML",
    country: "Mali",
    continent: "Africa",
    blurb: "Mosque in Djenné, Mali",
    pools: ["world","Africa","ML","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "sheikh-zayed-mosque",
    lat: 24.41171705993393,
    lng: 54.47480090725639,
    panoId: "xgg6Q3u48NQAAAQINSSVuA",
    heading: 291,
    pitch: 8,
    place: "Sheikh Zayed Mosque, Musaffah, United Arab Emirates",
    shortName: "Sheikh Zayed Mosque",
    countryCode: "AE",
    country: "United Arab Emirates",
    continent: "Asia",
    blurb: "Grand mosque in Abu Dhabi",
    pools: ["world","Asia","AE","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "lanse-aux-meadows",
    lat: 51.59655023896013,
    lng: -55.53347287998098,
    panoId: "7e5sFSF-Gb8ZFOYPzIpyxQ",
    heading: 133,
    pitch: 5,
    place: "L'Anse aux Meadows, Canada",
    shortName: "L'Anse aux Meadows",
    countryCode: "CA",
    country: "Canada",
    continent: "North America",
    blurb: "Archaeological site on the island of Newfoundland, Canada",
    pools: ["world","North America","CA","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "kronborg-castle",
    lat: 56.03865282113154,
    lng: 12.62380478588503,
    panoId: "eVDH4GGMgh2ngoQOa5EN0g",
    heading: 268,
    pitch: 8,
    place: "Kronborg Castle, Helsingør, Denmark",
    shortName: "Kronborg Castle",
    countryCode: "DK",
    country: "Denmark",
    continent: "Europe",
    blurb: "Castle in Helsingør, Denmark",
    pools: ["world","Europe","DK","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "pyramid-of-djoser",
    lat: 29.87064855532911,
    lng: 31.21707050715946,
    panoId: "MdW_naBq43sJpvlFBORzgQ",
    heading: 323,
    pitch: 8,
    place: "Pyramid of Djoser, Al Ḩawāmidīyah, Egypt",
    shortName: "Pyramid of Djoser",
    countryCode: "EG",
    country: "Egypt",
    continent: "Africa",
    blurb: "Archeological site in Egypt",
    pools: ["world","Africa","EG","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "tiryns",
    lat: 37.59907881802679,
    lng: 22.79859302043218,
    panoId: "iZ2Cm5eBDXLB3506e8tvrw",
    heading: 68,
    pitch: 8,
    place: "Tiryns, Néa Tírins, Greece",
    shortName: "Tiryns",
    countryCode: "GR",
    country: "Greece",
    continent: "Europe",
    blurb: "Ancient city and archaeological site in Argolis, Greece",
    pools: ["world","Europe","GR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "catherine-palace",
    lat: 59.71525591066914,
    lng: 30.39695484669618,
    panoId: "TRMM01YVVtTEqdrsK0FfUQ",
    heading: 317,
    pitch: 8,
    place: "Catherine Palace, Pushkin, Russia",
    shortName: "Catherine Palace",
    countryCode: "RU",
    country: "Russia",
    continent: "Europe",
    blurb: "Palace near St. Petersburg",
    pools: ["world","Europe","RU","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "hofburg-palace",
    lat: 48.20620853595332,
    lng: 16.36489898776848,
    panoId: "KbHOyiSmZpQ0o2hBDF8rlQ",
    heading: 54,
    pitch: 12,
    place: "Hofburg Palace, Innere Stadt, Austria",
    shortName: "Hofburg Palace",
    countryCode: "AT",
    country: "Austria",
    continent: "Europe",
    blurb: "Palace located in Vienna, Austria",
    pools: ["world","Europe","AT","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "vienna-state-opera-house",
    lat: 48.20424686720663,
    lng: 16.36814080736801,
    panoId: "GMD8S1mr9BmsasF0EHdmvg",
    heading: 143,
    pitch: 5,
    place: "Vienna State Opera house, Vienna, Austria",
    shortName: "Vienna State Opera house",
    countryCode: "AT",
    country: "Austria",
    continent: "Europe",
    blurb: "Opera house in Vienna, Austria",
    pools: ["world","Europe","AT","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "piazza-san-marco",
    lat: 45.43482104580119,
    lng: 12.33921865416923,
    panoId: "fpNJWjJWpjJr0QqpnX7ehA",
    heading: 226,
    pitch: 5,
    place: "Piazza San Marco, Venice, Italy",
    shortName: "Piazza San Marco",
    countryCode: "IT",
    country: "Italy",
    continent: "Europe",
    blurb: "Square in Venice, Italy",
    pools: ["world","Europe","IT","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "sanssouci-palace",
    lat: 52.40243501916299,
    lng: 13.03981999867378,
    panoId: "ONlzepiyLYI-TY3s0kkXNA",
    heading: 318,
    pitch: 5,
    place: "Sanssouci Palace, Potsdam, Germany",
    shortName: "Sanssouci Palace",
    countryCode: "DE",
    country: "Germany",
    continent: "Europe",
    blurb: "Palace in Brandenburg, Germany",
    pools: ["world","Europe","DE","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "dolby-theatre",
    lat: 34.10153054201699,
    lng: -118.3402597247546,
    panoId: "42dJn_QnQLV4rr5LYiDvyQ",
    heading: 359,
    pitch: 5,
    place: "Dolby Theatre, Hollywood, California",
    shortName: "Dolby Theatre",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "CA",
    state: "California",
    blurb: "Live entertainment theatre in Los Angeles, California, United States",
    pools: ["world","North America","US","US-CA","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "mariinsky-theatre",
    lat: 59.92517536902119,
    lng: 30.29654250374292,
    panoId: "QvgTHXM1dBuVgh51lJmtKQ",
    heading: 330,
    pitch: 12,
    place: "Mariinsky Theatre, Admiralteisky, Russia",
    shortName: "Mariinsky Theatre",
    countryCode: "RU",
    country: "Russia",
    continent: "Europe",
    blurb: "Opera company in Saint Petersburg, Russia",
    pools: ["world","Europe","RU","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "pyramid-of-khafre",
    lat: 29.97682741678443,
    lng: 31.13196496089586,
    panoId: "a8R-MFFhkhjZIo9bjedZIA",
    heading: 226,
    pitch: 5,
    place: "Pyramid of Khafre, Kirdāsah, Egypt",
    shortName: "Pyramid of Khafre",
    countryCode: "EG",
    country: "Egypt",
    continent: "Africa",
    blurb: "Smooth-sided pyramid in Giza, Egypt",
    pools: ["world","Africa","EG","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "lincoln-memorial",
    lat: 38.89015619890444,
    lng: -77.04944275999539,
    panoId: "rV1zQPC-txLOYF9DDy_vuw",
    heading: 212,
    pitch: 8,
    place: "Lincoln Memorial, Washington, District of Columbia",
    shortName: "Lincoln Memorial",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "DC",
    state: "District of Columbia",
    blurb: "Memorial in Washington, D.C., United States",
    pools: ["world","North America","US","US-DC","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "tower-of-hercules",
    lat: 43.3862798308352,
    lng: -8.40643973211408,
    panoId: "kb7b34fwRHpBV-N7hMoxdA",
    heading: 175,
    pitch: 12,
    place: "Tower of Hercules, A Coruña, Spain",
    shortName: "Tower of Hercules",
    countryCode: "ES",
    country: "Spain",
    continent: "Europe",
    blurb: "Ancient lighthouse in Spain",
    pools: ["world","Europe","ES","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "nymphenburg-palace",
    lat: 48.15892260461212,
    lng: 11.50744984637879,
    panoId: "BAxlFxWG4TSGraJeOX2CJA",
    heading: 251,
    pitch: 5,
    place: "Nymphenburg Palace, Pasing, Germany",
    shortName: "Nymphenburg Palace",
    countryCode: "DE",
    country: "Germany",
    continent: "Europe",
    blurb: "Baroque palace in Munich, Bavaria, southern Germany",
    pools: ["world","Europe","DE","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "jeronimos-monastery",
    lat: 38.69708495109946,
    lng: -9.206701913772248,
    panoId: "RWVO4-kck28lhMk6cbWfoQ",
    heading: 48,
    pitch: 5,
    place: "Jerónimos Monastery, Ajuda, Portugal",
    shortName: "Jerónimos Monastery",
    countryCode: "PT",
    country: "Portugal",
    continent: "Europe",
    blurb: "Building in Santa Maria de Belém, Lisbon District, Portugal",
    pools: ["world","Europe","PT","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "auditorio-de-tenerife",
    lat: 28.45674931113996,
    lng: -16.25216209986724,
    panoId: "FmZGjY8HCCPHGH61eCSx-w",
    heading: 131,
    pitch: 8,
    place: "Auditorio de Tenerife, Santa Cruz de Tenerife, Spain",
    shortName: "Auditorio de Tenerife",
    countryCode: "ES",
    country: "Spain",
    continent: "Europe",
    blurb: "Arts complex in Santa Cruz de Tenerife, Spain",
    pools: ["world","Europe","ES","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "an-tkabir",
    lat: 39.92599560127974,
    lng: 32.837141011027,
    panoId: "0fscc5GgEeIvOHsaCoLJeQ",
    heading: 189,
    pitch: 8,
    place: "Anıtkabir, Mebusevleri, Turkey",
    shortName: "Anıtkabir",
    countryCode: "TR",
    country: "Turkey",
    continent: "Asia",
    blurb: "Atatürk's mausoleum in Ankara, Turkey",
    pools: ["world","Asia","TR","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "kunsthistorisches-museum",
    lat: 48.2031838623933,
    lng: 16.36051293275355,
    panoId: "N19K0G3sCXE-CihW3CwNZg",
    heading: 46,
    pitch: 8,
    place: "Kunsthistorisches Museum, Innere Stadt, Austria",
    shortName: "Kunsthistorisches Museum",
    countryCode: "AT",
    country: "Austria",
    continent: "Europe",
    blurb: "Art museum in Vienna, Austria",
    pools: ["world","Europe","AT","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "palace-of-culture-and-science",
    lat: 52.231736646265,
    lng: 21.00771100430835,
    panoId: "af0ZpoeHnFaevSKO4ujzUQ",
    heading: 265,
    pitch: 8,
    place: "Palace of Culture and Science, Warsaw, Poland",
    shortName: "Palace of Culture and Science",
    countryCode: "PL",
    country: "Poland",
    continent: "Europe",
    blurb: "High-rise building and house of culture in Warsaw, Poland",
    pools: ["world","Europe","PL","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "charminar",
    lat: 17.36181010985134,
    lng: 78.47368817511662,
    panoId: "mkz6BM1cOxCXGaK2NXK5DA",
    heading: 102,
    pitch: 8,
    place: "Charminar, Hyderabad, India",
    shortName: "Charminar",
    countryCode: "IN",
    country: "India",
    continent: "Asia",
    blurb: "Mosque in Hyderabad, India",
    pools: ["world","Asia","IN","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "monte-alban",
    lat: 17.04412963911296,
    lng: -96.76780209488139,
    panoId: "iW_6Aw8wup0TlpkXbXMJpw",
    heading: 174,
    pitch: 12,
    place: "Monte Albán, San Francisco Javier, Mexico",
    shortName: "Monte Albán",
    countryCode: "MX",
    country: "Mexico",
    continent: "North America",
    blurb: "Pre-Columbian archaeological site in Mexico",
    pools: ["world","North America","MX","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "george-washington-bridge",
    lat: 40.85134929095791,
    lng: -73.95433304170477,
    panoId: "pInVyQv-bnJXdQOmd_8J1A",
    heading: 77,
    pitch: 5,
    place: "George Washington Bridge, Washington Heights, New York",
    shortName: "George Washington Bridge",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Bridge in New York City",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "changdeokgung",
    lat: 37.58047040190363,
    lng: 126.9921020725894,
    panoId: "uM2xDW3SuoBbp-zukpVULA",
    heading: 199,
    pitch: 5,
    place: "Changdeokgung, Seoul, South Korea",
    shortName: "Changdeokgung",
    countryCode: "KR",
    country: "South Korea",
    continent: "Asia",
    blurb: "Palace in Seoul, South Korea",
    pools: ["world","Asia","KR","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "taksim-square",
    lat: 41.03695341888468,
    lng: 28.98473380844521,
    panoId: "Q9mEUAe1oGnr-uMFQzZ0ew",
    heading: 91,
    pitch: 8,
    place: "Taksim Square, Eminönü, Turkey",
    shortName: "Taksim Square",
    countryCode: "TR",
    country: "Turkey",
    continent: "Asia",
    blurb: "Square in Istanbul, Turkey",
    pools: ["world","Asia","TR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "pyramid-of-the-sun",
    lat: 19.69287616897875,
    lng: -98.84485223130511,
    panoId: "l_xLfdLucf4lJN7oNBi2ag",
    heading: 111,
    pitch: 8,
    place: "Pyramid of the Sun, San Francisco Mazapa, Mexico",
    shortName: "Pyramid of the Sun",
    countryCode: "MX",
    country: "Mexico",
    continent: "North America",
    blurb: "Mesoamerica structure in Teotihuacan, first built in 100 CE",
    pools: ["world","North America","MX","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "brooklyn-museum",
    lat: 40.6715404143869,
    lng: -73.96317742316984,
    panoId: "1l2ePs0V35-94CYvR5tw0w",
    heading: 226,
    pitch: 8,
    place: "Brooklyn Museum, Park Slope, New York",
    shortName: "Brooklyn Museum",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Art museum in Brooklyn, New York",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "pattadakal-group-of-monuments",
    lat: 15.94747954432827,
    lng: 75.81658834828035,
    panoId: "OFQWI0rNq2zMYGuY-gy3NA",
    heading: 5,
    pitch: 8,
    place: "Pattadakal Group of Monuments, Gūdūr, India",
    shortName: "Pattadakal Group of Monuments",
    countryCode: "IN",
    country: "India",
    continent: "Asia",
    blurb: "World Heritage site with 7th and 8th century temples",
    pools: ["world","Asia","IN","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "grand-palace",
    lat: 13.75178811469879,
    lng: 100.4936474972627,
    panoId: "PyBmz-iUthtTIDRyaRX76Q",
    heading: 224,
    pitch: 5,
    place: "Grand Palace, Wat Arun, Thailand",
    shortName: "Grand Palace",
    countryCode: "TH",
    country: "Thailand",
    continent: "Asia",
    blurb: "Official residence of the King of Thailand since 1782",
    pools: ["world","Asia","TH","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "independence-hall",
    lat: 39.94913402781705,
    lng: -75.14994386406883,
    panoId: "XnDkma948OrnKoCuLcDaQg",
    heading: 190,
    pitch: 12,
    place: "Independence Hall, Old City, Pennsylvania",
    shortName: "Independence Hall",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "PA",
    state: "Pennsylvania",
    blurb: "Historic building in Philadelphia, Pennsylvania, USA",
    pools: ["world","North America","US","US-PA","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "1915-canakkale-bridge",
    lat: 40.33925264408313,
    lng: 26.63764673973786,
    panoId: "RY-cpIoOYUO_skncGCiNHg",
    heading: 311,
    pitch: 8,
    place: "1915 Çanakkale Bridge, Lapseki, Turkey",
    shortName: "1915 Çanakkale Bridge",
    countryCode: "TR",
    country: "Turkey",
    continent: "Asia",
    blurb: "Road suspension bridge in Turkey",
    pools: ["world","Asia","TR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "trump-tower",
    lat: 40.76252942184156,
    lng: -73.97429873971494,
    panoId: "seF-VIzfQcyvKWUQrvE5UQ",
    heading: 106,
    pitch: 12,
    place: "Trump Tower, Times Square, New York",
    shortName: "Trump Tower",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Skyscraper in Manhattan, New York",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "sequoia-national-park",
    lat: 36.56477100521538,
    lng: -118.7728582174504,
    panoId: "cv23twzum0TKNZYfMzal3w",
    heading: 127,
    pitch: 0,
    place: "Sequoia National Park, Three Rivers, California",
    shortName: "Sequoia National Park",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "CA",
    state: "California",
    blurb: "National park in the Sierra Nevada mountains, California, U.S.",
    pools: ["world","North America","US","US-CA","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "pyramid-of-menkaure",
    lat: 29.97325993640227,
    lng: 31.12750845580183,
    panoId: "Wy636-Z0BXZeL4hymzPCOg",
    heading: 137,
    pitch: 8,
    place: "Pyramid of Menkaure, Kirdāsah, Egypt",
    shortName: "Pyramid of Menkaure",
    countryCode: "EG",
    country: "Egypt",
    continent: "Africa",
    blurb: "Smallest of the three main Pyramids of Giza, Egypt",
    pools: ["world","Africa","EG","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "international-commerce-centre",
    lat: 22.3026127271096,
    lng: 114.1610630870221,
    panoId: "4KuPLt9yQssUuUrldkalzw",
    heading: 313,
    pitch: 5,
    place: "International Commerce Centre, China",
    shortName: "International Commerce Centre",
    countryCode: "CN",
    country: "China",
    continent: "Asia",
    blurb: "Skyscraper in West Kowloon, Hong Kong",
    pools: ["world","Asia","CN","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "great-mosque-of-kairouan",
    lat: 35.68210732583941,
    lng: 10.10305442561813,
    panoId: "M6DSjJPFgVk5Sddx-rviUw",
    heading: 137,
    pitch: 8,
    place: "Great Mosque of Kairouan, Kairouan, Tunisia",
    shortName: "Great Mosque of Kairouan",
    countryCode: "TN",
    country: "Tunisia",
    continent: "Africa",
    blurb: "Tunisian religious building and site of Islamic worship",
    pools: ["world","Africa","TN","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "museum-of-fine-arts-boston",
    lat: 42.34025083891054,
    lng: -71.09523613937174,
    panoId: "rKCYuDGTgUq8nhPlF-gZdw",
    heading: 136,
    pitch: 5,
    place: "Museum of Fine Arts Boston, West Fens, Massachusetts",
    shortName: "Museum of Fine Arts Boston",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "MA",
    state: "Massachusetts",
    blurb: "Art museum in Boston, Massachusetts, United States",
    pools: ["world","North America","US","US-MA","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "maidens-tower",
    lat: 41.02133111639534,
    lng: 29.00473006161277,
    panoId: "EMaRgyQpVyhWgHA7uitwMA",
    heading: 243,
    pitch: 8,
    place: "Maiden's Tower, Üsküdar, Turkey",
    shortName: "Maiden's Tower",
    countryCode: "TR",
    country: "Turkey",
    continent: "Asia",
    blurb: "Lighthouse at the southern entrance of the Bosphorus in Turkey",
    pools: ["world","Asia","TR","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "space-needle",
    lat: 47.62164056675058,
    lng: -122.3490034372154,
    panoId: "egcC2nBCNg1AISc1FfZoCA",
    heading: 183,
    pitch: 5,
    place: "Space Needle, Seattle, Washington",
    shortName: "Space Needle",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "WA",
    state: "Washington",
    blurb: "Observation tower in Seattle, Washington, United States",
    pools: ["world","North America","US","US-WA","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "iron-pillar-of-delhi",
    lat: 28.52367263241329,
    lng: 77.18496265483878,
    panoId: "tNMjhuIfQQp5sWaaXY5EEw",
    heading: 5,
    pitch: 8,
    place: "iron pillar of Delhi, Saiyad-ul-ajaib, India",
    shortName: "iron pillar of Delhi",
    countryCode: "IN",
    country: "India",
    continent: "Asia",
    blurb: "7.2 m pillar in the Qutb complex, Mehrauli, Delhi, India, erected in the 5th century CE",
    pools: ["world","Asia","IN","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "manhattan-bridge",
    lat: 40.7064773167622,
    lng: -73.99197308716785,
    panoId: "554F1owHn-m7aV90I_X5qA",
    heading: 49,
    pitch: 5,
    place: "Manhattan Bridge, Chinatown, New York",
    shortName: "Manhattan Bridge",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Bridge in New York City",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "amber-fort",
    lat: 26.98498109784558,
    lng: 75.85038326624651,
    panoId: "OAGs65MJh4Z0nJp6cJmdag",
    heading: 17,
    pitch: 8,
    place: "Amber Fort, Ākera, India",
    shortName: "Amber Fort",
    countryCode: "IN",
    country: "India",
    continent: "Asia",
    blurb: "Fort and palace in Rajasthan, India",
    pools: ["world","Asia","IN","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "canyonlands-national-park",
    lat: 38.16013243304956,
    lng: -109.7776767182228,
    panoId: "b6thxPhdHcnjIPCmx1_XUQ",
    heading: 64,
    pitch: 0,
    place: "Canyonlands National Park, Utah",
    shortName: "Canyonlands National Park",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "UT",
    state: "Utah",
    blurb: "U.S. National Park located in southeastern Utah near the town of Moab",
    pools: ["world","North America","US","US-UT","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "bayon",
    lat: 13.44126207208053,
    lng: 103.8587418678425,
    panoId: "agH3zOqEw8IWX1pNVIFDTA",
    heading: 200,
    pitch: 12,
    place: "Bayon, Angkor Thum, Cambodia",
    shortName: "Bayon",
    countryCode: "KH",
    country: "Cambodia",
    continent: "Asia",
    blurb: "Khmer temple in Cambodia",
    pools: ["world","Asia","KH","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "maya-ruins-of-tulum",
    lat: 20.21471732286641,
    lng: -87.43001362689363,
    panoId: "V2ej4wzgbV7mzX06tOlcfw",
    heading: 90,
    pitch: 8,
    place: "Maya Ruins of Tulum, Tulum, Mexico",
    shortName: "Maya Ruins of Tulum",
    countryCode: "MX",
    country: "Mexico",
    continent: "North America",
    blurb: "Protected area in Quintana Roo, Mexico",
    pools: ["world","North America","MX","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "badlands-national-park",
    lat: 43.74969594766377,
    lng: -102.5006421036077,
    panoId: "x_F7HfhZuNQjBeazSl7zNg",
    heading: 57,
    pitch: 0,
    place: "Badlands National Park, South Dakota",
    shortName: "Badlands National Park",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "SD",
    state: "South Dakota",
    blurb: "National park in South Dakota, United States",
    pools: ["world","North America","US","US-SD","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "jefferson-memorial",
    lat: 38.88088823312498,
    lng: -77.03666022013383,
    panoId: "p6VrO9jQFz_DKGXimWJqCA",
    heading: 359,
    pitch: 8,
    place: "Jefferson Memorial, The Wharf, District of Columbia",
    shortName: "Jefferson Memorial",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "DC",
    state: "District of Columbia",
    blurb: "Memorial in Washington, D.C., United States",
    pools: ["world","North America","US","US-DC","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "istiqlal-mosque",
    lat: -6.170589140289324,
    lng: 106.8302404454031,
    panoId: "TMNffQsqLCmSx94KwJxkgQ",
    heading: 34,
    pitch: 8,
    place: "Istiqlal Mosque, Jakarta, Indonesia",
    shortName: "Istiqlal Mosque",
    countryCode: "ID",
    country: "Indonesia",
    continent: "Asia",
    blurb: "Mosque in Jakarta, Indonesia",
    pools: ["world","Asia","ID","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "tajin-veracruz",
    lat: 20.44883262585581,
    lng: -97.3773851237498,
    panoId: "5d6jGV8f9GIaI8JSCmchRw",
    heading: 226,
    pitch: 5,
    place: "Tajín Veracruz, Congregación el Tajín, Mexico",
    shortName: "Tajín Veracruz",
    countryCode: "MX",
    country: "Mexico",
    continent: "North America",
    blurb: "Archaeological site in Mexico",
    pools: ["world","North America","MX","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "xochicalco",
    lat: 18.80358363095072,
    lng: -99.29589014324809,
    panoId: "RiaEqzaP8ICPul8I_4iYUA",
    heading: 316,
    pitch: 8,
    place: "Xochicalco, Xochicalco (Cirenio Longares), Mexico",
    shortName: "Xochicalco",
    countryCode: "MX",
    country: "Mexico",
    continent: "North America",
    blurb: "Xochicalco is an unesco world heritage site in Xochicalco (Cirenio Longares), Mexico.",
    pools: ["world","North America","MX","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "temple-of-kukulcan",
    lat: 20.68275061019346,
    lng: -88.56831221177477,
    panoId: "SW5A-dmUWfNOrH0l47Kr9g",
    heading: 296,
    pitch: 12,
    place: "Temple of Kukulcan, Chichén-Itzá, Mexico",
    shortName: "Temple of Kukulcan",
    countryCode: "MX",
    country: "Mexico",
    continent: "North America",
    blurb: "Pyramid in Chichén Itzám",
    pools: ["world","North America","MX","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "gateway-arch",
    lat: 38.62471712163498,
    lng: -90.18718340512014,
    panoId: "8e9YHrPnyTWnfqY87uQ1Hw",
    heading: 94,
    pitch: 5,
    place: "Gateway Arch, St. Louis, Missouri",
    shortName: "Gateway Arch",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "MO",
    state: "Missouri",
    blurb: "Monument in St. Louis, Missouri",
    pools: ["world","North America","US","US-MO","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "875-north-michigan-avenue",
    lat: 41.89893191076369,
    lng: -87.62420701857661,
    panoId: "T8hcPsn9q1i4dNDf_nhNRA",
    heading: 93,
    pitch: 8,
    place: "875 North Michigan Avenue, Near North Side, Illinois",
    shortName: "875 North Michigan Avenue",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "IL",
    state: "Illinois",
    blurb: "Skyscraper in Chicago, Illinois, United States",
    pools: ["world","North America","US","US-IL","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "bulguksa",
    lat: 35.78703248482746,
    lng: 129.3325184501419,
    panoId: "S2MEFHuOAKqxbMbyng9D-g",
    heading: 355,
    pitch: 5,
    place: "Bulguksa, Gyeongju, South Korea",
    shortName: "Bulguksa",
    countryCode: "KR",
    country: "South Korea",
    continent: "Asia",
    blurb: "Bulguksa is a temple in Gyeongju, South Korea.",
    pools: ["world","Asia","KR","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "trump-international-hotel-and-tower",
    lat: 41.88901938996298,
    lng: -87.62508431814324,
    panoId: "20jrCfvDCHiGE7Iy2gk27g",
    heading: 274,
    pitch: 5,
    place: "Trump International Hotel and Tower, Chicago Loop, Illinois",
    shortName: "Trump International Hotel and Tower",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "IL",
    state: "Illinois",
    blurb: "Skyscraper condo-hotel in downtown Chicago, Illinois",
    pools: ["world","North America","US","US-IL","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "historic-villages-of-shirakawa-go-and-gokayama",
    lat: 36.2566628217667,
    lng: 136.9066594464368,
    panoId: "sTL8iCAaepr158tShjuBHA",
    heading: 322,
    pitch: 12,
    place: "Historic Villages of Shirakawa-gō and Gokayama, Hida, Japan",
    shortName: "Historic Villages of Shirakawa-gō and Gokayama",
    countryCode: "JP",
    country: "Japan",
    continent: "Asia",
    blurb: "World Heritage Site in Japan",
    pools: ["world","Asia","JP","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "merdeka-118",
    lat: 3.142836137836098,
    lng: 101.7006978710353,
    panoId: "arwJVQqoMufhx38-6Y_xRQ",
    heading: 179,
    pitch: 5,
    place: "Merdeka 118, Bukit Bintang, Malaysia",
    shortName: "Merdeka 118",
    countryCode: "MY",
    country: "Malaysia",
    continent: "Asia",
    blurb: "Skyscraper in Kuala Lumpur, Malaysia",
    pools: ["world","Asia","MY","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "shuri-castle",
    lat: 26.21699489845006,
    lng: 127.7192776876075,
    panoId: "4rV57J6hRvRmVto5d6VYZg",
    heading: 79,
    pitch: 12,
    place: "Shuri Castle, Kanegusuku, Japan",
    shortName: "Shuri Castle",
    countryCode: "JP",
    country: "Japan",
    continent: "Asia",
    blurb: "Shuri Castle is a ryukyuan gusuku castle in Kanegusuku, Japan.",
    pools: ["world","Asia","JP","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "western-wall-plaza",
    lat: 31.77528052347584,
    lng: 35.2336882800904,
    panoId: "_xHLFYfiUTyOqMb3jyYUPQ",
    heading: 359,
    pitch: 5,
    place: "Western Wall Plaza, Giv`at H̱ananya, Israel",
    shortName: "Western Wall Plaza",
    countryCode: "IL",
    country: "Israel",
    continent: "Asia",
    blurb: "Public square in the Old City of Jerusalem",
    pools: ["world","Asia","IL","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "jvari",
    lat: 41.83856677272843,
    lng: 44.73441925639194,
    panoId: "iHBwDXzldWTVYNAcJ-8hLw",
    heading: 258,
    pitch: 8,
    place: "Jvari, Mtskheta, Georgia",
    shortName: "Jvari",
    countryCode: "GE",
    country: "Georgia",
    continent: "Asia",
    blurb: "Monastery in Georgia (country)",
    pools: ["world","Asia","GE","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "capitol-reef-national-park",
    lat: 38.20794264960046,
    lng: -111.1838407699036,
    panoId: "uZXkadUaI1GKCEWTWkcOGA",
    heading: 121,
    pitch: 0,
    place: "Capitol Reef National Park, Utah",
    shortName: "Capitol Reef National Park",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "UT",
    state: "Utah",
    blurb: "National park in Wayne and Garfield counties in Utah, United State",
    pools: ["world","North America","US","US-UT","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "cairo-citadel",
    lat: 30.02847578952046,
    lng: 31.26104887312394,
    panoId: "hPnlieuseaIBOFe6bS9HgQ",
    heading: 18,
    pitch: 8,
    place: "Cairo Citadel, Masākin Shirkat Abū al Wafā lil Muqāwalāt wa al Istithmār al ‘Aqārī, Egypt",
    shortName: "Cairo Citadel",
    countryCode: "EG",
    country: "Egypt",
    continent: "Africa",
    blurb: "Medieval Islamic-era fortification in Cairo, Egypt",
    pools: ["world","Africa","EG","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "23-marina",
    lat: 25.09012161557995,
    lng: 55.15089185222349,
    panoId: "OjJQnJ1MUEqMG-SHVeG45Q",
    heading: 219,
    pitch: 12,
    place: "23 Marina, Dubai Marina, United Arab Emirates",
    shortName: "23 Marina",
    countryCode: "AE",
    country: "United Arab Emirates",
    continent: "Asia",
    blurb: "23 Marina is a dubai skyscraper in Dubai Marina, United Arab Emirates.",
    pools: ["world","Asia","AE","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "brazilian-national-archives",
    lat: -22.90647969610557,
    lng: -43.19048197209192,
    panoId: "I8t6VKgB9XQxMYB5Dor3QQ",
    heading: 260,
    pitch: 12,
    place: "Brazilian National Archives, Rio de Janeiro, Brazil",
    shortName: "Brazilian National Archives",
    countryCode: "BR",
    country: "Brazil",
    continent: "South America",
    blurb: "National archives of Brazil",
    pools: ["world","South America","BR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "lotte-world-tower",
    lat: 37.51135058124229,
    lng: 127.1024846016689,
    panoId: "WCgLTeU7CJHHXcRtPt0-Vw",
    heading: 1,
    pitch: 5,
    place: "Lotte World Tower, Seongnam-si, South Korea",
    shortName: "Lotte World Tower",
    countryCode: "KR",
    country: "South Korea",
    continent: "Asia",
    blurb: "Skyscraper in Seoul, South Korea",
    pools: ["world","Asia","KR","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "national-mosque-of-malaysia",
    lat: 3.14268378840293,
    lng: 101.690755368299,
    panoId: "sRMeHdvBftnszI3cENOJ_A",
    heading: 129,
    pitch: 5,
    place: "National Mosque of Malaysia, Kuala Lumpur, Malaysia",
    shortName: "National Mosque of Malaysia",
    countryCode: "MY",
    country: "Malaysia",
    continent: "Asia",
    blurb: "Mosque in Kuala Lumpur, Malaysia",
    pools: ["world","Asia","MY","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "walt-disney-concert-hall",
    lat: 34.0560024862194,
    lng: -118.2491411716622,
    panoId: "rZTtFJLN8HQ4OiP4RziWRQ",
    heading: 224,
    pitch: 8,
    place: "Walt Disney Concert Hall, Los Angeles, California",
    shortName: "Walt Disney Concert Hall",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "CA",
    state: "California",
    blurb: "Concert hall in Los Angeles, California, U.S.",
    pools: ["world","North America","US","US-CA","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "colon-theater",
    lat: -34.60034401097563,
    lng: -58.38298385069975,
    panoId: "ya3w9V3LP1wVXZuXBGgGJA",
    heading: 186,
    pitch: 8,
    place: "Colón Theater, Buenos Aires, Argentina",
    shortName: "Colón Theater",
    countryCode: "AR",
    country: "Argentina",
    continent: "South America",
    blurb: "Opera house in Buenos Aires",
    pools: ["world","South America","AR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "national-museum-of-western-art",
    lat: 35.71534016332942,
    lng: 139.7744158977907,
    panoId: "Npxoi04Q7_8qthduHgym8g",
    heading: 89,
    pitch: 5,
    place: "National Museum of Western Art, Taito, Japan",
    shortName: "National Museum of Western Art",
    countryCode: "JP",
    country: "Japan",
    continent: "Asia",
    blurb: "Art museum in Tokyo, Japan",
    pools: ["world","Asia","JP","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "sultan-qaboos-grand-mosque",
    lat: 23.5856747501509,
    lng: 58.38985822030279,
    panoId: "7euRn-PF5N5rP6yx8V55vw",
    heading: 200,
    pitch: 5,
    place: "Sultan Qaboos Grand Mosque, Bawshar, Oman",
    shortName: "Sultan Qaboos Grand Mosque",
    countryCode: "OM",
    country: "Oman",
    continent: "Asia",
    blurb: "Mosque in Muscat, Sultanate of Oman",
    pools: ["world","Asia","OM","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "metlife-building",
    lat: 40.75392756355438,
    lng: -73.97791793906865,
    panoId: "0HSmsgarJD3N8bKGx3mG_A",
    heading: 122,
    pitch: 5,
    place: "MetLife Building, Times Square, New York",
    shortName: "MetLife Building",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Office skyscraper in Manhattan, New York",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "dharahara",
    lat: 27.70063277005687,
    lng: 85.31163115124059,
    panoId: "i1TOtIm0YDYOxrj7SlC46A",
    heading: 118,
    pitch: 12,
    place: "Dharahara, Kathmandu, Nepal",
    shortName: "Dharahara",
    countryCode: "NP",
    country: "Nepal",
    continent: "Asia",
    blurb: "Nine storey tower built in 1832",
    pools: ["world","Asia","NP","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "elmina-castle",
    lat: 5.083373498100297,
    lng: -1.34832787266717,
    panoId: "m1eXDwHGu4kzDb2iCpT3ag",
    heading: 158,
    pitch: 8,
    place: "Elmina Castle, Elmina, Ghana",
    shortName: "Elmina Castle",
    countryCode: "GH",
    country: "Ghana",
    continent: "Africa",
    blurb: "Fortified colonial trading post in Ghana",
    pools: ["world","Africa","GH","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "muhammad-ali-mosque",
    lat: 30.02836561736986,
    lng: 31.25927628599496,
    panoId: "6kQ52R-ecasrZegaNhiT1w",
    heading: 58,
    pitch: 8,
    place: "Muhammad Ali Mosque, Masākin Shirkat Abū al Wafā lil Muqāwalāt wa al Istithmār al ‘Aqārī, Egypt",
    shortName: "Muhammad Ali Mosque",
    countryCode: "EG",
    country: "Egypt",
    continent: "Africa",
    blurb: "Muhammad Ali Mosque is a mosque in egypt in Masākin Shirkat Abū al Wafā lil Muqāwalāt wa al Istithmār al ‘Aqārī, Egypt.",
    pools: ["world","Africa","EG","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "emirates-office-tower",
    lat: 25.21645396824058,
    lng: 55.28330144552687,
    panoId: "QnNNEKrTKQptBc-UzV1aMA",
    heading: 2,
    pitch: 8,
    place: "Emirates Office Tower, Dubai International Financial Centre, United Arab Emirates",
    shortName: "Emirates Office Tower",
    countryCode: "AE",
    country: "United Arab Emirates",
    continent: "Asia",
    blurb: "54-floor office building along Sheikh Zayed Road in the city of Dubai, United Arab Emirates.",
    pools: ["world","Asia","AE","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "ho-chi-minh-mausoleum",
    lat: 21.03682803957123,
    lng: 105.8366076146292,
    panoId: "kAk3vRqpzf5QzVVrP3FIUQ",
    heading: 265,
    pitch: 5,
    place: "Ho Chi Minh Mausoleum, Yên Phụ, Vietnam",
    shortName: "Ho Chi Minh Mausoleum",
    countryCode: "VN",
    country: "Vietnam",
    continent: "Asia",
    blurb: "National mausoleum in Hanoi, Vietnam",
    pools: ["world","Asia","VN","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "williamsburg-bridge",
    lat: 40.71345700048741,
    lng: -73.97129611030584,
    panoId: "YUz4BBdxlcrnyjbP4bl6iA",
    heading: 115,
    pitch: 5,
    place: "Williamsburg Bridge, Williamsburg, New York",
    shortName: "Williamsburg Bridge",
    countryCode: "US",
    country: "United States",
    continent: "North America",
    stateCode: "NY",
    state: "New York",
    blurb: "Bridge in New York City",
    pools: ["world","North America","US","US-NY","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "palacio-do-planalto",
    lat: -15.7995732571202,
    lng: -47.8610642637868,
    panoId: "iWRbmb9qmZq1NkH--iEUmg",
    heading: 21,
    pitch: 8,
    place: "Palácio do Planalto, Plano Piloto, Brazil",
    shortName: "Palácio do Planalto",
    countryCode: "BR",
    country: "Brazil",
    continent: "South America",
    blurb: "Palace in Brasília, Brazil",
    pools: ["world","South America","BR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "las-lajas-sanctuary",
    lat: 0.8045379741425385,
    lng: -77.58522027666599,
    panoId: "vOSlLdE-mgDAn0uVeRIyAw",
    heading: 321,
    pitch: 5,
    place: "Las Lajas Sanctuary, Potosí, Colombia",
    shortName: "Las Lajas Sanctuary",
    countryCode: "CO",
    country: "Colombia",
    continent: "South America",
    blurb: "National monument of Colombia",
    pools: ["world","South America","CO","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "african-renaissance-monument",
    lat: 14.72210548257458,
    lng: -17.49593076580592,
    panoId: "jd8bFC7roaBafYOm21EkPg",
    heading: 83,
    pitch: 8,
    place: "African Renaissance Monument, Mermoz Boabab, Senegal",
    shortName: "African Renaissance Monument",
    countryCode: "SN",
    country: "Senegal",
    continent: "Africa",
    blurb: "Memorial outside of Dakar, Senegal",
    pools: ["world","Africa","SN","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "museum-of-new-zealand-te-papa-tongarewa",
    lat: -41.2894445089136,
    lng: 174.7825659669882,
    panoId: "cyu1kK5lZ4SUz9RBFaagHA",
    heading: 203,
    pitch: 5,
    place: "Museum of New Zealand Te Papa Tongarewa, Te Aro, New Zealand",
    shortName: "Museum of New Zealand Te Papa Tongarewa",
    countryCode: "NZ",
    country: "New Zealand",
    continent: "Oceania",
    blurb: "National museum of New Zealand",
    pools: ["world","Oceania","NZ","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "abuja-national-mosque",
    lat: 9.06093358759231,
    lng: 7.493613030526948,
    panoId: "h9-dd-tJP24dK6N5LrPbxw",
    heading: 265,
    pitch: 5,
    place: "Abuja National Mosque, Abuja, Nigeria",
    shortName: "Abuja National Mosque",
    countryCode: "NG",
    country: "Nigeria",
    continent: "Africa",
    blurb: "Mosque in Nigeria",
    pools: ["world","Africa","NG","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "mosque-of-omar-ibn-al-khattab",
    lat: 11.37807420766476,
    lng: -72.23452402666939,
    panoId: "2lvSlt5Ur7Xz7CAEv9cW2A",
    heading: 103,
    pitch: 12,
    place: "Mosque of Omar Ibn Al-Khattab, Maicao, Colombia",
    shortName: "Mosque of Omar Ibn Al-Khattab",
    countryCode: "CO",
    country: "Colombia",
    continent: "South America",
    blurb: "Mosque in Colombia",
    pools: ["world","South America","CO","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "san-ignacio-mini",
    lat: -27.2555235170849,
    lng: -55.53048957573861,
    panoId: "S5BnLMe48Kwv0kvW6-OT6Q",
    heading: 284,
    pitch: 8,
    place: "San Ignacio Miní, San Ignacio, Argentina",
    shortName: "San Ignacio Miní",
    countryCode: "AR",
    country: "Argentina",
    continent: "South America",
    blurb: "UNESCO World Heritage Site in Misiones Province, Argentina",
    pools: ["world","South America","AR","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "gran-torre-costanera",
    lat: -33.41601007819501,
    lng: -70.60767493078194,
    panoId: "PGTKAe3epoRDzSyOshWAaw",
    heading: 138,
    pitch: 5,
    place: "Gran Torre Costanera, Providencia, Chile",
    shortName: "Gran Torre Costanera",
    countryCode: "CL",
    country: "Chile",
    continent: "South America",
    blurb: "Skyscraper in Chile",
    pools: ["world","South America","CL","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "art-gallery-of-ontario",
    lat: 43.6540074328526,
    lng: -79.39289900585675,
    panoId: "YEmhWnPSkb-pBdYnm_Fs0g",
    heading: 168,
    pitch: 12,
    place: "Art Gallery of Ontario, Kensington-Chinatown, Canada",
    shortName: "Art Gallery of Ontario",
    countryCode: "CA",
    country: "Canada",
    continent: "North America",
    blurb: "Art museum in Toronto, Ontario, Canada",
    pools: ["world","North America","CA","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "pumapunku",
    lat: -16.56138340722657,
    lng: -68.67944421199628,
    panoId: "l2dFExw1UFByYuMK1q-NoA",
    heading: 182,
    pitch: 12,
    place: "Pumapunku, Tiahuanaco, Bolivia",
    shortName: "Pumapunku",
    countryCode: "BO",
    country: "Bolivia",
    continent: "South America",
    blurb: "Terraced platform mound of the Tiwanaku culture",
    pools: ["world","South America","BO","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "rio-de-janeiro-cathedral",
    lat: -22.91014487138909,
    lng: -43.17985114026745,
    panoId: "IdqJAmOX6fkhPc5TpOifxA",
    heading: 225,
    pitch: 5,
    place: "Rio de Janeiro Cathedral, Rio de Janeiro, Brazil",
    shortName: "Rio de Janeiro Cathedral",
    countryCode: "BR",
    country: "Brazil",
    continent: "South America",
    blurb: "Cathedral in Rio de Janeiro, Brazil",
    pools: ["world","South America","BR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "eldorado-business-tower",
    lat: -23.57262164548906,
    lng: -46.69744144921862,
    panoId: "o4UzSdu37f49SeiSM7IcnA",
    heading: 195,
    pitch: 8,
    place: "Eldorado Business Tower, Pinheiros, Brazil",
    shortName: "Eldorado Business Tower",
    countryCode: "BR",
    country: "Brazil",
    continent: "South America",
    blurb: "Skyscraper in São Paulo, Brazil",
    pools: ["world","South America","BR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "sao-paulo-cathedral",
    lat: -23.55238328172451,
    lng: -46.63441272874543,
    panoId: "1QnZcoywwhD52ILvy0_Tmg",
    heading: 2,
    pitch: 5,
    place: "São Paulo Cathedral, São Paulo, Brazil",
    shortName: "São Paulo Cathedral",
    countryCode: "BR",
    country: "Brazil",
    continent: "South America",
    blurb: "Cathedral in Brazil",
    pools: ["world","South America","BR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "amazon-theatre",
    lat: -3.130637186547129,
    lng: -60.02450455209431,
    panoId: "ZK-PU7vaE0tIdjO4aFe_yg",
    heading: 73,
    pitch: 5,
    place: "Amazon Theatre, Manaus, Brazil",
    shortName: "Amazon Theatre",
    countryCode: "BR",
    country: "Brazil",
    continent: "South America",
    blurb: "Opera house in Manaus, Brazil",
    pools: ["world","South America","BR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "afrikaans-language-monument",
    lat: -33.76639684542447,
    lng: 18.94386549042821,
    panoId: "UyqR403zpgXummQHt37dzA",
    heading: 307,
    pitch: 5,
    place: "Afrikaans Language Monument, Paarl, South Africa",
    shortName: "Afrikaans Language Monument",
    countryCode: "ZA",
    country: "South Africa",
    continent: "Africa",
    blurb: "Afrikaans Language Monument is an architectural structure in Paarl, South Africa.",
    pools: ["world","Africa","ZA","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "recoleta-cemetery",
    lat: -34.58927658999176,
    lng: -58.39307917076335,
    panoId: "0P19__b3GK_DHZpck4G5Pg",
    heading: 1,
    pitch: 5,
    place: "Recoleta Cemetery, Retiro, Argentina",
    shortName: "Recoleta Cemetery",
    countryCode: "AR",
    country: "Argentina",
    continent: "South America",
    blurb: "Cemetery in Buenos Aires, Argentina",
    pools: ["world","South America","AR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "al-zaytunah-mosque",
    lat: 36.79664859094605,
    lng: 10.17059430082532,
    panoId: "ESN7HP1N4MLGj856Lain4A",
    heading: 36,
    pitch: 8,
    place: "Al-Zaytunah Mosque, Tunis, Tunisia",
    shortName: "Al-Zaytunah Mosque",
    countryCode: "TN",
    country: "Tunisia",
    continent: "Africa",
    blurb: "Mosque in Tunisia",
    pools: ["world","Africa","TN","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "palacio-de-lopez",
    lat: -25.27602527996545,
    lng: -57.63773922262578,
    panoId: "wf2mgOXTPUBv8cb4KTClKw",
    heading: 175,
    pitch: 5,
    place: "Palacio de López, Asunción, Paraguay",
    shortName: "Palacio de López",
    countryCode: "PY",
    country: "Paraguay",
    continent: "South America",
    blurb: "Administrative Office of the President and Executive Power of the Republic of Paraguay",
    pools: ["world","South America","PY","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "sutherland-falls",
    lat: -44.79809732434852,
    lng: 167.7328965607436,
    panoId: "qs-inhU3nv7PYV-GmGEF_g",
    heading: 220,
    pitch: 0,
    place: "Sutherland Falls, New Zealand",
    shortName: "Sutherland Falls",
    countryCode: "NZ",
    country: "New Zealand",
    continent: "Oceania",
    blurb: "Waterfall near Milford Sound in New Zealand's South Island",
    pools: ["world","Oceania","NZ","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "skylon-tower",
    lat: 43.08525367495658,
    lng: -79.08127039579978,
    panoId: "c45s_yDEg6Rci_wHGd9Dgw",
    heading: 89,
    pitch: 5,
    place: "Skylon Tower, Niagara Falls, Canada",
    shortName: "Skylon Tower",
    countryCode: "CA",
    country: "Canada",
    continent: "North America",
    blurb: "Observation tower in Niagara Falls, Canada",
    pools: ["world","North America","CA","landmarks"],
    qualityTier: 1,
    verified: true,
  },
  {
    id: "sambadrome-marques-de-sapucai",
    lat: -22.91139009826006,
    lng: -43.19685702021218,
    panoId: "MeVsYmZxSx6gawBa4Gvgfw",
    heading: 42,
    pitch: 12,
    place: "Sambadrome Marquês de Sapucaí, Cidade Nova, Brazil",
    shortName: "Sambadrome Marquês de Sapucaí",
    countryCode: "BR",
    country: "Brazil",
    continent: "South America",
    blurb: "Parade area in Rio de Janeiro, Brazil",
    pools: ["world","South America","BR","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "museum-of-contemporary-art-australia",
    lat: -33.85918009567327,
    lng: 151.2098132395457,
    panoId: "KPvx2vFN15Kg1Jd-Fp_fjw",
    heading: 223,
    pitch: 5,
    place: "Museum of Contemporary Art Australia, The Rocks, Australia",
    shortName: "Museum of Contemporary Art Australia",
    countryCode: "AU",
    country: "Australia",
    continent: "Oceania",
    blurb: "Art museum in Sydney, Australia",
    pools: ["world","Oceania","AU","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "port-campbell-national-park",
    lat: -38.64660220237397,
    lng: 143.0705545635242,
    panoId: "v0_7_M7X7cj-0STyZw72Bg",
    heading: 240,
    pitch: 0,
    place: "Port Campbell National Park, Timboon, Australia",
    shortName: "Port Campbell National Park",
    countryCode: "AU",
    country: "Australia",
    continent: "Oceania",
    blurb: "National park in Australia",
    pools: ["world","Oceania","AU","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "australian-war-memorial",
    lat: -35.28122016557871,
    lng: 149.1495591503228,
    panoId: "B6-FUoNvwWh8QpuXlzWr_Q",
    heading: 331,
    pitch: 8,
    place: "Australian War Memorial, Campbell, Australia",
    shortName: "Australian War Memorial",
    countryCode: "AU",
    country: "Australia",
    continent: "Oceania",
    blurb: "National war memorial and museum in Canberra, Australia",
    pools: ["world","Oceania","AU","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "carlton-centre",
    lat: -26.2049559094829,
    lng: 28.04662967219233,
    panoId: "Mm8Q58UeCI1ooTwsLOGg3A",
    heading: 177,
    pitch: 8,
    place: "Carlton Centre, Johannesburg, South Africa",
    shortName: "Carlton Centre",
    countryCode: "ZA",
    country: "South Africa",
    continent: "Africa",
    blurb: "Skyscraper in Johannesburg, South Africa",
    pools: ["world","Africa","ZA","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "st-patricks-cathedral",
    lat: -37.81097582250479,
    lng: 144.9751493417435,
    panoId: "Acnxrdtue8JPixp0dlGhjQ",
    heading: 38,
    pitch: 5,
    place: "St Patrick's Cathedral, Melbourne City Centre, Australia",
    shortName: "St Patrick's Cathedral",
    countryCode: "AU",
    country: "Australia",
    continent: "Oceania",
    blurb: "Cathedral in Melbourne, Victoria, Australia",
    pools: ["world","Oceania","AU","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "cape-coast-castle",
    lat: 5.103843199331479,
    lng: -1.24057022726717,
    panoId: "5EImGuEKAABLpgsLT2hN8Q",
    heading: 249,
    pitch: 8,
    place: "Cape Coast Castle, Cape Coast, Ghana",
    shortName: "Cape Coast Castle",
    countryCode: "GH",
    country: "Ghana",
    continent: "Africa",
    blurb: "Former fortified colonial trading post in Ghana",
    pools: ["world","Africa","GH","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "rova-of-antananarivo",
    lat: -18.92412644512363,
    lng: 47.5326083099248,
    panoId: "i-SoG1KlbSSNv-jPSFNk5g",
    heading: 325,
    pitch: 8,
    place: "Rova of Antananarivo, Antananarivo, Madagascar",
    shortName: "Rova of Antananarivo",
    countryCode: "MG",
    country: "Madagascar",
    continent: "Africa",
    blurb: "Royal complex in Madagascar",
    pools: ["world","Africa","MG","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "church-of-cristo-obrero-y-nuestra-senora-de-lour",
    lat: -34.74345476464165,
    lng: -55.76688159762799,
    panoId: "sJvRMFCMXNlVnn832ULr4A",
    heading: 127,
    pitch: 8,
    place: "Church of Cristo Obrero y Nuestra Señora de Lourdes, Atlántida, Uruguay",
    shortName: "Church of Cristo Obrero y Nuestra Señora de Lourdes",
    countryCode: "UY",
    country: "Uruguay",
    continent: "South America",
    blurb: "Church in Uruguay",
    pools: ["world","South America","UY","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "chilean-national-museum-of-fine-arts",
    lat: -33.43540430459335,
    lng: -70.64303633900826,
    panoId: "ZTND4Zk91naRr-vCPkneTg",
    heading: 280,
    pitch: 8,
    place: "Chilean National Museum of Fine Arts, Santiago, Chile",
    shortName: "Chilean National Museum of Fine Arts",
    countryCode: "CL",
    country: "Chile",
    continent: "South America",
    blurb: "National monument of Chile",
    pools: ["world","South America","CL","landmarks"],
    qualityTier: 4,
    verified: true,
  },
  {
    id: "zaculeu",
    lat: 15.33285931258524,
    lng: -91.49264168685305,
    panoId: "MnPgar7TD24NGt0yx-kX3Q",
    heading: 354,
    pitch: 8,
    place: "Zaculeu, Huehuetenango, Guatemala",
    shortName: "Zaculeu",
    countryCode: "GT",
    country: "Guatemala",
    continent: "North America",
    blurb: "Archaeological site in Guatemala",
    pools: ["world","North America","GT","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "hvalsey",
    lat: 60.82892117213025,
    lng: -45.78172368337206,
    panoId: "TZ_VevJWBD1fUbJqmR4x8g",
    heading: 171,
    pitch: 8,
    place: "Hvalsey, Narsaq, Greenland",
    shortName: "Hvalsey",
    countryCode: "GL",
    country: "Greenland",
    continent: "North America",
    blurb: "Historic norse settlement in Greenland",
    pools: ["world","North America","GL","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "cristo-de-la-concordia",
    lat: -17.38421286328009,
    lng: -66.13496789829414,
    panoId: "IMBWut-BAcQbQIv8mO8paw",
    heading: 162,
    pitch: 12,
    place: "Cristo de la Concordia, Cochabamba, Bolivia",
    shortName: "Cristo de la Concordia",
    countryCode: "BO",
    country: "Bolivia",
    continent: "South America",
    blurb: "Colossal statue of Jesus, tourist attraction, and pilgrimage site in Cochabamba in Bolivia",
    pools: ["world","South America","BO","landmarks"],
    qualityTier: 2,
    verified: true,
  },
  {
    id: "primary-cathedral-of-bogota",
    lat: 4.598726676155914,
    lng: -74.0761163977525,
    panoId: "_Rubft7QZt4MWDQC3q3d9Q",
    heading: 131,
    pitch: 5,
    place: "Primary Cathedral of Bogotá, Bogotá, Colombia",
    shortName: "Primary Cathedral of Bogotá",
    countryCode: "CO",
    country: "Colombia",
    continent: "South America",
    blurb: "National monument of Colombia",
    pools: ["world","South America","CO","landmarks"],
    qualityTier: 3,
    verified: true,
  },
  {
    id: "buenos-aires-stock-exchange",
    lat: -34.60422594190133,
    lng: -58.36929990153455,
    panoId: "ZRom-z2RQngs7oTSTe7hTw",
    heading: 286,
    pitch: 5,
    place: "Buenos Aires Stock Exchange, Buenos Aires, Argentina",
    shortName: "Buenos Aires Stock Exchange",
    countryCode: "AR",
    country: "Argentina",
    continent: "South America",
    blurb: "Stock exchange located in Buenos Aires, Argentina",
    pools: ["world","South America","AR","landmarks"],
    qualityTier: 4,
    verified: true,
  },
];

export const LANDMARKS_PACK: LocationPack = {
  version: "landmarks-2",
  generatedAt: "2024-01-01T00:00:00.000Z",
  locations: CHUNK_0,
};
